/**
 * Pure 동(building) label planning: DXF sidecar labels + CCTV IP pairs → drawable plan.
 *
 * 동 labels come from the DXF (extract_building_labels.py) in the same meter-offset
 * coords as the CCTV IP sidecar. We fit a sidecar→editor similarity from
 * (CCTV IP sidecar point ↔ placed camera) pairs and apply it to each building
 * label. Reuses the zone import's similarity fit (uniform scale + translation +
 * optional Y flip). JointJS-agnostic and side-effect free so unit tests inject
 * plain data.
 */

import {
  applyTransform,
  fitSimilarityFromPairs,
  MIN_SIMILARITY_PAIRS,
  type SimilarityPair,
} from '../../zone-auto-import/lib/transform';
import type { BuildingLabel, DongPlan, PlannedDong } from '../model/types';

/**
 * Building label: 2–3 digits with an OPTIONAL 동 (101동 or bare 101/102). Mirrors
 * the backend `\d{2,3}동?` filter — a final guard before drawing.
 */
export const DONG_NAME_RE = /^\d{2,3}동?$/;

/**
 * Build the 동 placement plan.
 *
 * Throws a user-facing `Error` when fewer than {@link MIN_SIMILARITY_PAIRS}
 * CCTV IP pairs exist — IPs must be applied first to anchor the transform.
 */
export function buildDongPlan(
  buildings: BuildingLabel[],
  cctvPairs: SimilarityPair[],
  existingDongNames: string[]
): DongPlan {
  if (cctvPairs.length < MIN_SIMILARITY_PAIRS) {
    throw new Error(
      'IP가 매핑된 CCTV가 부족해 동 좌표를 맞출 수 없습니다. 먼저 CCTV IP/이름을 적용하세요.'
    );
  }
  const transform = fitSimilarityFromPairs(cctvPairs);

  const existing = new Set(existingDongNames);
  const seen = new Set<string>();
  const dongs: PlannedDong[] = [];
  const skippedExisting: string[] = [];
  for (const b of buildings) {
    if (!DONG_NAME_RE.test(b.name)) continue;
    if (seen.has(b.name)) continue; // 같은 동 번호 중복 → 1개만
    seen.add(b.name);
    if (existing.has(b.name)) {
      skippedExisting.push(b.name);
      continue;
    }
    const p = applyTransform(transform, { x: b.x, y: b.y });
    dongs.push({ name: b.name, point: { x: p.x, y: p.y } });
  }

  return {
    dongs,
    skippedExisting,
    transform: {
      medResidual: transform.medResidual,
      pairCount: transform.pairCount,
      inlierCount: transform.inlierCount,
    },
  };
}
